<template>
    <div>
        <bs5-page-header :title="$t('users.title')" :subtitle="$t('users.subtitle')">
            <template #actions>
                <bs5-base-button variant="secondary" :loading="loading" @click="fetchUsers">
                    <i v-if="!loading" class="bi bi-arrow-clockwise"></i>{{ $t("common.refresh") }}
                </bs5-base-button>
                <bs5-base-button variant="primary" @click="showAddUserModal = true">
                    <i class="bi bi-plus-lg"></i>{{ $t("users.new") }}
                </bs5-base-button>
            </template>
        </bs5-page-header>

        <div class="au-card">
            <div class="au-card-head">
                <h2 class="au-card-title"><i class="bi bi-people"></i>{{ $t("users.list") }}</h2>
                <div class="au-search ms-auto">
                    <i class="bi bi-search"></i>
                    <input v-model="search" type="search" class="form-control" :placeholder="$t('users.search')"
                        :aria-label="$t('users.search')" />
                </div>
            </div>

            <div v-if="loading && users.length === 0" class="au-empty">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ $t("common.loading") }}</span>
                </div>
            </div>
            <div v-else-if="users.length === 0" class="au-empty">
                <i class="bi bi-people"></i>
                <div class="au-empty-title">{{ $t("users.emptyTitle") }}</div>
                <div>{{ $t("users.emptyText") }}</div>
            </div>
            <div v-else-if="filteredUsers.length === 0" class="au-empty">
                <i class="bi bi-search"></i>
                <div>{{ $t("users.noResults") }}</div>
            </div>
            <div v-else class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th><i class="bi bi-person me-1"></i>{{ $t("users.user") }}</th>
                            <th><i class="bi bi-shield-check me-1"></i>{{ $t("users.role") }}</th>
                            <th><i class="bi bi-shop me-1"></i>{{ $t("users.distributors") }}</th>
                            <th class="text-end"><i class="bi bi-lightning-charge me-1"></i>{{ $t("common.actions") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="user in filteredUsers" :key="user.id as string">
                            <td>
                                <div class="au-entity">
                                    <div class="au-avatar user-initials">{{ getInitials(user) }}</div>
                                    <div style="min-width: 0;">
                                        <div class="au-entity-name">{{ user.fullName }}</div>
                                        <div class="au-entity-sub">
                                            <i class="bi bi-envelope"></i>{{ user.email }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <span class="au-chip" :class="`is-${getRoleStyle(user.role).tone}`">
                                    <i class="bi" :class="getRoleStyle(user.role).icon"></i>
                                    {{ $t(getRoleStyle(user.role).labelKey) }}
                                </span>
                            </td>
                            <td>
                                <div v-if="isLoadingDistributors(user)">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                                        <span class="visually-hidden">{{ $t("common.loading") }}</span>
                                    </div>
                                </div>
                                <div v-else-if="getUserDistributors(user).length > 0" class="d-flex flex-wrap gap-2">
                                    <bs5-badge-with-close v-for="distributor in getUserDistributors(user)"
                                        :key="distributor.id as string" :label="distributor.name"
                                        :close-label="$t('users.removeDistributor', { name: distributor.name })"
                                        @remove="removeDistributor(user, distributor)" />
                                </div>
                                <span v-else class="users-muted">{{ $t("users.noDistributors") }}</span>
                            </td>
                            <td>
                                <div class="au-actions">
                                    <bs5-icon-button icon="bi-pencil" :label="$t('common.edit')"
                                        @click="openEditModal(user)" />
                                    <bs5-icon-button icon="bi-key" tone="amber" :label="$t('users.changePassword')"
                                        @click="openSetPasswordModal(user)" />
                                    <bs5-icon-button icon="bi-diagram-3" tone="indigo"
                                        :label="$t('users.assignDistributor')"
                                        @click="openAssignDistributorModal(user)" />
                                    <bs5-icon-button icon="bi-trash3" tone="danger" :label="$t('common.delete')"
                                        :loading="removeLoading" @click="removeUser(user)" />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="users.length > 0 && filteredUsers.length > 0" class="au-card-foot">
                <span>{{ $t("users.total", { count: filteredUsers.length }) }}</span>
            </div>
        </div>

        <AddUserModal :visible="showAddUserModal" @close="showAddUserModal = false" @user-added="fetchUsers" />
        <EditUserModal :visible="showEditUserModal" :userId="selectedUserId" @close="showEditUserModal = false"
            @user-updated="fetchUsers" />
        <SetPasswordModal :visible="showSetPasswordModal" :userId="selectedUserId" @close="showSetPasswordModal = false"
            @password-set="fetchUsers" />
        <AssignDistributorModal :visible="showAssignDistributorModal" :userId="selectedUserId"
            @close="showAssignDistributorModal = false" @distributor-assigned="fetchUsers" />
    </div>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, ref } from "vue";
import userService from "@/services/user.service";
import { User } from "@/models/user.model";
import { UserRole } from "@/enums/user-role.enum";
import { Distributor } from "@/models/distributor.model";
import AddUserModal from "./modals/AddUserModal.vue";
import EditUserModal from "./modals/EditUserModal.vue";
import SetPasswordModal from "./modals/SetPasswordModal.vue";
import AssignDistributorModal from "./modals/AssignDistributorModal.vue";
import { showToast } from "@/utils/toast";

type RoleStyle = { labelKey: string; icon: string; tone: string };

// Klucz tłumaczenia (roles.*), ikona i odcień znacznika dla każdej roli
const roleStyles: Record<number, RoleStyle> = {
    [UserRole.SuperAdmin]: { labelKey: "roles.SuperAdmin", icon: "bi-shield-lock", tone: "rose" },
    [UserRole.Distributor]: { labelKey: "roles.Distributor", icon: "bi-shop", tone: "cyan" },
    [UserRole.Client]: { labelKey: "roles.Client", icon: "bi-person", tone: "indigo" },
};
const unknownRoleStyle: RoleStyle = { labelKey: "users.unknownRole", icon: "bi-question-circle", tone: "amber" };

export default defineComponent({
    components: {
        AddUserModal,
        EditUserModal,
        SetPasswordModal,
        AssignDistributorModal
    },
    data() {
        return {
            users: ref<User[]>([]), // Używamy typowania z User.ts
            search: "",
            loading: ref(false),
            removeLoading: ref(false),
            showAddUserModal: ref(false), // Nowy stan modala
            showEditUserModal: ref(false),
            showSetPasswordModal: ref(false),
            showAssignDistributorModal: ref(false),
            selectedUserId: ref<string>(""),
            userDistributors: ref<{ [key: string]: Distributor[] }>({}),
            loadingDistributors: ref<{ [key: string]: boolean }>({}),
        };
    },
    computed: {
        filteredUsers(): User[] {
            const phrase = this.search.trim().toLocaleLowerCase();
            if (!phrase) {
                return this.users;
            }
            return this.users.filter(user =>
                (user.fullName ?? "").toLocaleLowerCase().includes(phrase)
                || (user.email ?? "").toLocaleLowerCase().includes(phrase));
        },
    },
    methods: {
        async fetchUsers() {
            try {
                this.loading = true;
                this.users = await userService.getUsers();
                await this.fetchDistributorsForUsers();
            } catch (error) {
                showToast(t("common.loadFailed"), "error");
                console.error("Error fetching users:", error);
            } finally {
                this.loading = false;
            }
        },
        async fetchDistributorsForUsers() {
            for (const user of this.users) {
                await this.fetchDistributorsForUser(user);
            }
        },

        async fetchDistributorsForUser(user: User) {
            try {
                this.loadingDistributors[user.id as string] = true;
                this.userDistributors[user.id as string] = await userService.getUserDistributor(user.id as string);
            } catch (error) {
                console.error(`Error fetching distributors for user ${user.id}:`, error);
                this.userDistributors[user.id as string] = [];
            } finally {
                this.loadingDistributors[user.id as string] = false;
            }
        },
        async removeUser(user: User) {
            const response = await this.$swal.fire({
                title: t("users.deleteTitle", { name: user.fullName }),
                text: t("common.confirmDeleteText"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("common.confirmDeleteButton"),
                cancelButtonText: t("common.cancel"),
            });
            if (!response.isConfirmed) {
                return;
            }
            try {
                this.removeLoading = true;
                await userService.deleteUser(user.id as string);
                showToast(t("users.deleted"), "success");
                await this.fetchUsers();
            } catch (error) {
                showToast(t("users.deleteFailed"), "error");
                console.error("Error deleting user:", error);
            } finally {
                this.removeLoading = false;
            }
        },
        async removeDistributor(user: User, distributor: Distributor) {
            try {
                await userService.removeDistributor(user.id as string, distributor.id as string);
                await this.fetchDistributorsForUser(user);
                showToast(t("users.distributorRemoved"), "success");
            } catch (e) {
                showToast(t("users.removeDistributorFailed"), "error");
                console.error("Error removing distributor:", e);
            }
        },
        getRoleStyle(role: UserRole): RoleStyle {
            return roleStyles[role] ?? unknownRoleStyle;
        },
        getInitials(user: User): string {
            const words = (user.fullName ?? "").trim().split(/\s+/).filter(word => word.length > 0);
            if (words.length === 0) {
                return (user.email ?? "?").charAt(0).toLocaleUpperCase();
            }
            if (words.length === 1) {
                return words[0].charAt(0).toLocaleUpperCase();
            }
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toLocaleUpperCase();
        },
        getUserDistributors(user: User): Distributor[] {
            return this.userDistributors[user.id as string] ?? [];
        },
        isLoadingDistributors(user: User): boolean {
            // Do czasu pobrania listy (kolejka pobierań jest sekwencyjna) pokazujemy spinner zamiast "Brak"
            return this.loadingDistributors[user.id as string] === true
                || this.userDistributors[user.id as string] === undefined;
        },
        openEditModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showEditUserModal = true;
        },
        openSetPasswordModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showSetPasswordModal = true;
        },
        openAssignDistributorModal(user: User) {
            this.selectedUserId = user.id as string;
            this.showAssignDistributorModal = true;
        },
    },
    mounted() {
        this.fetchUsers();
    },
});
</script>

<style scoped>
.user-initials {
    font-size: 15px;
    letter-spacing: 0;
}

.users-muted {
    color: var(--au-muted);
}
</style>
